/**
 * mapview-constants README
 *
 * Purpose:
 *  - Central place for map tuning constants that affect UX (timings, z-index offsets, cache TTLs).
 *  - Keep values here small, well-documented, and easy to tweak without searching the codebase.
 *
 * Notes:
 *  - Central place for map tuning constants that affect UX (timings, z-index offsets, cache TTLs).
 *  - Tune conservatively; changes here affect interactivity and load. Document any project-specific
 *    decisions near the constant if deviating from the suggested ranges.
 */

import * as Leaflet from 'leaflet';

/**
 * Set the zoom level for different map scales.
 * @default {1000:12,5000:11,7000:10,10000:9}
 */
export const ZOOM_LEVELS: { [key: number]: number } = {
  1000: 12,
  5000: 11,
  7000: 10,
  10000: 9,
};

/**
 * Default zoom level used when no site-area thresholds match.
 * @default 1
 */
export const DEFAULT_ZOOM_LEVEL = 12;

/**
 * Default marker icon url.
 * @default './../assets/img/map-icons/map-marker-blue-active@2x.png'
 */
export const ICON_URL = './../assets/img/map-icons/map-marker-blue-active@2x.png';

/**
 * Default marker shadow url.
 * @default './assets/marker-shadow.png'
 */
export const SHADOW_URL = './assets/marker-shadow.png';

/**
 * Standard icon pixel size [width, height].
 * @default [24, 41]
 * @range width: 8-128, height: 8-128
 */
export const ICON_SIZE: [number, number] = [24, 41];

/**
 * Standard icon anchor point [x, y].
 * @default [12, 41]
 */
export const ICON_ANCHOR: [number, number] = [12, 41];

/**
 * Popup anchor offset relative to icon anchor [x, y].
 * @default [1, -34]
 */
export const POPUP_ANCHOR: [number, number] = [1, -34];

/**
 * Tooltip anchor offset relative to icon anchor [x, y].
 * @default [16, -28]
 */
export const TOOLTIP_ANCHOR: [number, number] = [16, -28];

/**
 * Observation icon pixel size [width, height].
 * @default [24, 47]
 * @range width: 8-128, height: 8-256
 */
export const OBSERVATION_ICON_SIZE: [number, number] = [24, 47];

/**
 * Default icon used for map markers when no domain-specific icon is available.
 */
export const ICON_DEFAULT = Leaflet.icon({
  iconUrl: ICON_URL,
  shadowUrl: SHADOW_URL,
  iconSize: ICON_SIZE,
  iconAnchor: ICON_ANCHOR,
  popupAnchor: POPUP_ANCHOR,
  tooltipAnchor: TOOLTIP_ANCHOR,
});

/**
 * Icon used for observation markers.
 */
export const OBSERVATION_ICON_MARKER = Leaflet.icon({
  iconUrl: '../../assets/img/map-icons/observation-icon.png',
  iconSize: OBSERVATION_ICON_SIZE,
  iconAnchor: ICON_ANCHOR,
  popupAnchor: POPUP_ANCHOR,
  tooltipAnchor: TOOLTIP_ANCHOR,
});

/**
 * Names of panes used in the Leaflet map for layering different types of content.
 */
export const PANE_NAMES = {
  GEOM_BASE: 'geomBasePane',
  MARKER: 'markerPane',
};

/**
 * Z-index values for map panes to control rendering order.
 * Higher values are rendered on top of lower values.
 * @default {API_BASE:400,API_WAI:450,MARKER:600,GEOM_BASE_API:300,GEOM_BASE_WAI:500}
 */
export const PANE_ZINDEX = {
  API_BASE: 400,
  MARKER: 600,
  GEOM_BASE_API: 300,
};

/**
 * Options for the marker cluster group (leaflet.markercluster).
 *
 * Observation markers and monitoring/environmental data source markers are the
 * densest marker types on the map, so they share one cluster group. Clusters zoom
 * to their bounds on click and spiderfy at max zoom; the hover coverage polygon is
 * disabled to keep the map readable.
 *
 * @default {maxClusterRadius:60, showCoverageOnHover:false}
 */
export const MARKER_CLUSTER_OPTIONS: Leaflet.MarkerClusterGroupOptions = {
  maxClusterRadius: 30,
  showCoverageOnHover: false,
  clusterPane: PANE_NAMES.MARKER,
};

/**
 * Colors associated with different domains for map markers and geometries.
 */
export const DOMAIN_COLORS: any = {
  Hau: '#e4f527e6',
  Moana: '#d9dfff',
  Wai: '#00a0ba',
  Whenua: '#dd7000e6',
};

/**
 * Icons associated with different domains for map markers.
 */
export const DOMAIN_MARKERS: any = {
  Hau: 'marker-hau.png',
  Moana: 'marker-blue.png',
  Wai: 'marker-blue.png',
  Whenua: 'marker-green.png',
};

/**
 * Z-index offset used for observation markers when highlighted (e.g., after click).
 * @default 2000
 */
export const OBSERVATION_ZINDEX_OFFSET_HIGHLIGHT = 2000;

/**
 * Z-index offset used for observation markers by default.
 * @default 200
 */
export const OBSERVATION_ZINDEX_OFFSET_NORMAL = 200;

/**
 * Duration in milliseconds to keep the observation marker highlighted (z-index elevated).
 * @default 1000
 * @range 250 - 5000
 */
export const OBSERVATION_HIGHLIGHT_DURATION_MS = 1000;

/**
 * Debounce for search term updates that trigger marker reloads (ms).
 * @default 1000
 * @range 250 - 1500
 */
export const SEARCH_TERM_DEBOUNCE_MS = 1000;

/**
 * How long to cache environmental data sources (ms).
 * @default 60000
 * @range 30000 - 300000
 */
export const ENV_SOURCES_CACHE_TTL_MS = 60000;

/**
 * Popup hover close delay (ms) used when the mouse leaves a popup before auto-closing.
 * @default 300
 * @range 100 - 500
 */
export const POPUP_HOVER_CLOSE_DELAY_MS = 300;

/**
 * Delay in milliseconds to reset styles/z-index after a click on popups/markers.
 * @default 1000
 * @range 500 - 3000
 */
export const POPUP_CLICK_RESET_DELAY_MS = 1000;

/**
 * Enable debug view for popups, showing pane and z-index.
 */
export const POPUP_DEBUG_VIEW = false;

/**
 * Developer aid: when true, a hover popup stays open after the mouse leaves so
 * its formatting can be inspected and tweaked. Set back to false for normal
 * hover-to-show behaviour.
 * @default false
 */
export const POPUP_ALWAYS_OPEN = false;
